import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import { useHeroIdentity } from 'hooks/sto/useHeroIdentity'
import { EligibilityCheckFooter } from 'components/sto/KeyActions'
import { Bar, Description, Identity, Title } from './StickyHeader.styles'

/** True once the element with `id` has scrolled up past the top of the viewport. */
const useScrolledPast = (id: string) => {
  const [past, setPast] = useState(false)
  useEffect(() => {
    const target = document.getElementById(id)
    if (!target || typeof IntersectionObserver === 'undefined') return undefined
    const observer = new IntersectionObserver(([entry]) =>
      setPast(!entry.isIntersecting && entry.boundingClientRect.bottom < 0),
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [id])
  return past
}

/**
 * How much the bar drops to fit: 0 everything, 1 without the description, the name on up to two
 * lines. Measured again from 0 on every resize and title change, so it only steps down when the
 * fuller one does not fit.
 */
const useFitLevel = (titleRef: RefObject<HTMLElement | null>, title: string) => {
  const [level, setLevel] = useState(0)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const onResize = () => {
      setLevel(0)
      setWidth(window.innerWidth)
    }
    window.addEventListener('resize', onResize)
    // A first measure in the fallback font can be too narrow.
    let active = true
    document.fonts?.ready.then(() => active && onResize())
    return () => {
      active = false
      window.removeEventListener('resize', onResize)
    }
  }, [])
  useLayoutEffect(() => setLevel(0), [title])
  useLayoutEffect(() => {
    const element = titleRef.current
    if (level < 1 && element && element.scrollWidth > element.clientWidth + 1) setLevel(level + 1)
  }, [titleRef, level, width, title])
  return level
}

/**
 * Desktop product bar (Figma 4914:81574): the product name, its ticker and description, and the
 * eligibility check, shown once KeyActions has scrolled out of view. A name too long for one line
 * drops the description, never the name itself.
 */
export const StickyHeader = () => {
  const state = useHeroIdentity()
  const visible = useScrolledPast('key-actions')
  const titleRef = useRef<HTMLParagraphElement>(null)
  const title = state.status === 'ready' ? state.data.title : ''
  const level = useFitLevel(titleRef, title)
  if (state.status !== 'ready') return null

  const { ticker, description } = state.data
  const summary = [ticker, description].filter(Boolean).join(' - ')

  return (
    <Bar $visible={visible} aria-hidden={!visible} inert={!visible}>
      <Identity>
        <Title ref={titleRef} $wrap={level === 1}>
          {title}
        </Title>
        {summary && level === 0 && <Description>{summary}</Description>}
      </Identity>
      <EligibilityCheckFooter layout="header" />
    </Bar>
  )
}
